import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowClockwise, CheckCircle, Spinner, Tray } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { dmsApi } from './dmsApi';
import { STAGE_VIEW, bandOf, BAND_VIEW } from './docsUi';

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('ja-JP', {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const ItemChip = ({ item }) => {
  if (item.stage === 'review') {
    const b = BAND_VIEW[bandOf(item)];
    return <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${b.box} ${b.text}`}>{b.label}</span>;
  }
  const st = STAGE_VIEW[item.stage] || STAGE_VIEW.uploading;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${st.cls}`}>
      {st.busy && <Spinner size={11} className="animate-spin" />}{st.label}
    </span>
  );
};

/** 取り込みの確認（全件）— 開いている取り込みを、バッチごとに新しい順でまとめる。 */
export default function IngestOpenListPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [userId, setUserId] = useState('');
  const [retrying, setRetrying] = useState('');

  const load = useCallback(async () => {
    try { setData(await dmsApi.ingestOpen(userId || undefined)); }
    catch { setData({ batches: [], counts: {} }); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="py-20 text-center text-sm text-zinc-400">読み込み中...</p>;
  const c = data.counts || {};
  const batches = data.batches || [];

  return (
    <div className="max-w-[1100px] mx-auto" data-testid="dms-ingest-open-page">
      <button type="button" onClick={() => navigate('/documents')} data-testid="dms-ingest-open-back"
        className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-teal-700 transition-colors mb-1">
        <ArrowLeft size={14} /> ドキュメントに戻る
      </button>
      <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <Tray size={20} weight="duotone" className="text-teal-700" /> 取り込みの確認
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5" data-testid="dms-ingest-open-counts">
            確認待ち {c.needs_review || 0}件
            {c.reading ? `（読み取り中 ${c.reading}件）` : ''}
            {c.failed ? ` ・ 失敗 ${c.failed}件` : ''}
            {` ・ 取り込み ${batches.length}件`}
          </p>
        </div>
        {data.is_admin && (
          <select value={userId} onChange={(e) => setUserId(e.target.value)}
            data-testid="dms-ingest-open-user-filter"
            className="h-9 rounded-md border border-zinc-200 bg-white px-2 text-sm text-zinc-700">
            <option value="">全員</option>
            {(data.users || []).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        )}
      </div>

      {batches.length === 0 ? (
        <div className="rounded-lg border border-zinc-200 bg-white py-16 text-center" data-testid="dms-ingest-open-empty">
          <CheckCircle size={34} weight="duotone" className="text-emerald-500 mx-auto mb-2" />
          <p className="text-base text-zinc-800 font-medium">確認が必要な取り込みはありません</p>
        </div>
      ) : (
        <div className="space-y-5">
          {batches.map((b) => {
            const bc = b.counts || {};
            const done = bc.done || 0;
            return (
              <section key={b.id} className="rounded-lg border border-zinc-200 bg-white overflow-hidden"
                data-testid={`dms-ingest-open-batch-${b.id}`}>
                <div className="flex items-center gap-3 flex-wrap border-b border-zinc-100 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">
                      {when(b.created_at)} の取り込み — {bc.total || 0}件
                      {data.is_admin && b.created_by_name ? `（${b.created_by_name}）` : ''}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      保存先: {b.folder_name || '—'} ・ 完了 {done}/{bc.total || 0}
                      {bc.needs_review ? ` ・ 確認待ち ${bc.needs_review}件` : ''}
                      {bc.reading ? ` ・ 読み取り中 ${bc.reading}件` : ''}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {bc.failed > 0 && (
                      <Button variant="outline" size="sm" disabled={retrying === b.id}
                        className="h-8 text-sm border-zinc-200"
                        data-testid={`dms-ingest-open-retry-${b.id}`}
                        onClick={async () => {
                          setRetrying(b.id);
                          try {
                            const r = await dmsApi.ingestRetryFailed(b.id);
                            toast.success(r.queued ? `${r.queued}件を読み直しています` : '読み直す対象がありません');
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || '再読み取りに失敗しました'); }
                          finally { setRetrying(''); }
                        }}>
                        <ArrowClockwise size={13} className="mr-1.5" />失敗した分を再読み取り
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="h-8 text-sm border-zinc-200"
                      data-testid={`dms-ingest-open-batch-link-${b.id}`}
                      onClick={() => navigate(`/documents/ingest/${b.id}`)}>
                      この取り込みを開く
                    </Button>
                  </div>
                </div>
                <ul className="divide-y divide-zinc-100">
                  {(b.items || []).map((i) => (
                    <li key={i.id}>
                      <button type="button"
                        onClick={() => navigate(`/documents/ingest/${b.id}?item=${i.id}`)}
                        data-testid={`dms-ingest-open-item-${i.id}`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-50 transition-colors">
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-zinc-900 truncate">
                            {i.suggestion?.title || i.filename}
                          </span>
                          <span className="block text-xs text-zinc-400 truncate">{i.filename}</span>
                        </span>
                        <ItemChip item={i} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
